import { allLanguages } from "../../../../../shared/consts";
import type { PatientProfilePatient } from "../../../patientProfileModal.types";
import type {
  PatientAllergy,
  PatientFieldsDraft,
  StaffingPreferenceField,
  StaffingPreferences,
  StaffingPreferencesBody,
} from "./staffingPreferences.types";

export const languageOptions = allLanguages.map((language) => ({
  value: language,
  label: language,
}));

export const kidsCountOptions = [0, 1, 2, 3, 4, 5, 6].map((count) => ({
  value: count,
  label: String(count),
}));

export function getLanguageSelectValue(value: string | null) {
  return value !== null && allLanguages.includes(value) ? value : null;
}

export function createPatientFieldsDraft(patient: PatientProfilePatient): PatientFieldsDraft {
  return {
    allergyIds: patient.allergies.map((allergy) => allergy.id),
    medicalNotes: patient.medicalNotes ?? "",
  };
}

export function createStaffingPreferencesDraft(
  preferences: StaffingPreferences
): StaffingPreferencesBody {
  return {
    allowMultipleCaregivers: preferences.allowMultipleCaregivers ?? null,
    bedbound: preferences.bedbound ?? null,
    cane: preferences.cane ?? null,
    gender: preferences.gender ?? null,
    halal: preferences.halal ?? null,
    hoyerLift: preferences.hoyerLift ?? null,
    kids: preferences.kids ?? null,
    kidsCount: preferences.kidsCount ?? null,
    kosher: preferences.kosher ?? null,
    mainLanguage: preferences.mainLanguage ?? null,
    pets: preferences.pets ?? null,
    petsDetails: preferences.petsDetails ?? null,
    requests: preferences.requests ?? null,
    secondaryLanguage: preferences.secondaryLanguage ?? null,
    shabat: preferences.shabat ?? null,
    smoking: preferences.smoking ?? null,
    smokingCaregiver: preferences.smokingCaregiver ?? null,
    tasks: preferences.tasks ?? null,
    walker: preferences.walker ?? null,
    wheelchair: preferences.wheelchair ?? null,
  };
}

export function getKidsCountValue(preferences: StaffingPreferences) {
  if (preferences.kids === false) {
    return "0";
  }

  return formatStaffingValue(preferences.kidsCount);
}

export function getStringListValue(preferences: StaffingPreferences) {
  const value = (preferences.petsDetails ?? []).join(", ");

  return value === "" ? "Unknown" : value;
}

export function formatStaffingValue(value: unknown) {
  if (value === null || value === undefined || value === "") {
    return "Unknown";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  return String(value);
}

export function toNullableBoolean(value: unknown) {
  return typeof value === "boolean" ? value : null;
}

export function getAllergyOptions(
  allergies: PatientAllergy[],
  selectedAllergies: PatientAllergy[]
) {
  const allergyById = new Map<number, PatientAllergy>();

  allergies.forEach((allergy) => allergyById.set(allergy.id, allergy));
  selectedAllergies.forEach((allergy) => allergyById.set(allergy.id, allergy));

  return Array.from(allergyById.values())
    .map((allergy) => ({ value: allergy.id, label: allergy.name }))
    .sort((left, right) => left.label.localeCompare(right.label));
}

export function getAllergiesLabel(allergies: PatientAllergy[]) {
  return allergies.length > 0
    ? allergies.map((allergy) => allergy.name).join(", ")
    : "No known allergies";
}

export const staffingPreferenceColumns: StaffingPreferenceField[][] = [
  [
    { label: "Main language", key: "mainLanguage" },
    { label: "Secondary language", key: "secondaryLanguage" },
    { label: "Has kids in house", key: "kids", type: "boolean" },
    { label: "Number of kids in house", key: "kidsCount", getValue: getKidsCountValue },
    { label: "Smoking patient", key: "smoking", type: "boolean" },
    { label: "Work with smoking caregiver", key: "smokingCaregiver", type: "boolean" },
  ],
  [
    { label: "Is patient bedbound", key: "bedbound", type: "boolean" },
    { label: "Is patient with cane", key: "cane", type: "boolean" },
    { label: "Is patient with walker", key: "walker", type: "boolean" },
    { label: "Is patient with wheelchair", key: "wheelchair", type: "boolean" },
    { label: "Patient has kosher house", key: "kosher", type: "boolean" },
    { label: "Patient has halal culture", key: "halal", type: "boolean" },
  ],
  [
    { label: "Prefered gender", key: "gender" },
    { label: "Patient is shabat observant", key: "shabat", type: "boolean" },
    { label: "Allow Multiple Caregivers", key: "allowMultipleCaregivers", type: "boolean" },
    { label: "Using hoyer lift", key: "hoyerLift", type: "boolean" },
    { label: "Patient has pets", key: "pets", type: "boolean" },
    { label: "Pet details", key: "petsDetails", getValue: getStringListValue },
  ],
];
